/**
 * Pure derivations over the shared profile payload. Kept framework-free and
 * unit-tested so the hero, chart, and scrubber can share one consistent view of
 * "the scans" and "a metric's value at a scan" without re-deriving it each place.
 */
import type {
  APISharedMetricSeries,
  APISharedMetricValue,
  APISharedProfileResponse,
} from '@see-stella/types'
import { sDayJs } from '@see-stella/utils'

export interface Scan {
  scanId: string
  scanAt: string
}

/**
 * The distinct scans actually shown, oldest → newest. Every metric shares the
 * same scans (keyed by scanId); we dedupe and sort by timestamp so the chart
 * x-axis and the photo scrubber agree on the ordering and the index.
 */
export function collectScans(profile: APISharedProfileResponse): Scan[] {
  const byId = new Map<string, string>()
  for (const series of profile.metrics) {
    for (const v of series.data) byId.set(v.scanId, v.scanAt)
  }
  return [...byId.entries()]
    .map(([scanId, scanAt]) => ({ scanId, scanAt }))
    .sort((a, b) => sDayJs(a.scanAt).valueOf() - sDayJs(b.scanAt).valueOf())
}

/** The metric series for a techName, or undefined. */
export function seriesFor(
  profile: APISharedProfileResponse,
  techName: string
): APISharedMetricSeries | undefined {
  return profile.metrics.find((m) => m.techName === techName)
}

/** A metric's value at a specific scan, or undefined if it wasn't measured. */
export function valueAt(
  series: APISharedMetricSeries,
  scanId: string
): APISharedMetricValue | undefined {
  return series.data.find((v) => v.scanId === scanId)
}

export interface ScanPhoto {
  imageUrl: string
  landmarkImageWidth?: number | null
  landmarkImageHeight?: number | null
}

/**
 * The scan photo + frame dims for a scanId, resolved from ANY metric that has a
 * value at that scan. `imageUrl` and the landmark frame dims are properties of
 * the SCAN — identical across every metric's value for that scanId — so they must
 * NOT be read from only the focused metric, which may not have measured this scan
 * (the scan list is a union across metrics). Returns undefined when no photo is
 * available (photos not opted in → every imageUrl is '').
 */
export function photoForScan(
  profile: APISharedProfileResponse,
  scanId: string
): ScanPhoto | undefined {
  for (const series of profile.metrics) {
    const v = valueAt(series, scanId)
    if (v?.imageUrl) {
      return {
        imageUrl: v.imageUrl,
        landmarkImageWidth: v.landmarkImageWidth,
        landmarkImageHeight: v.landmarkImageHeight,
      }
    }
  }
  return undefined
}

/** Round an EMA to the 1–99 display integer the dots and tags show. */
export function displayScore(ema: number | null | undefined): number | null {
  if (ema === null || ema === undefined || Number.isNaN(ema)) return null
  return Math.max(1, Math.min(99, Math.round(ema)))
}

/**
 * A metric's line color as a CSS value. Only a subset of the ~37 tech names
 * have a dedicated `--m-*` token; the rest fall back to teal so the dot border
 * is always a valid color rather than an empty/invalid custom property.
 */
export function metricColorVar(techName: string): string {
  return `var(--m-${techName}, var(--teal))`
}

/**
 * Project a landmark point (normalized 0..1 to the ORIGINAL scan image) into the
 * fixed-aspect photo frame, which shows the image with `object-fit: cover`.
 * Cover scales the image to fill the frame and CROPS the overflow, so a raw
 * `x%/y%` placement drifts whenever the image aspect differs from the frame's
 * (the scans are 9:16 but the frame is 4/5). This maps the point through the
 * same cover scale + centered crop so a dot lands on the right spot. Returns
 * normalized {left, top} in FRAME space (0..1). Falls back to the raw point when
 * the image dimensions are unknown.
 */
export function projectCoverPoint(
  x: number,
  y: number,
  imgW: number | null | undefined,
  imgH: number | null | undefined,
  frameAspect: number // frame width / height
): { left: number; top: number } {
  if (!imgW || !imgH || imgW <= 0 || imgH <= 0 || frameAspect <= 0) {
    return { left: x, top: y }
  }
  const imgAspect = imgW / imgH
  // The dimension that overflows the frame is scaled by this ratio (>1); the
  // other maps 1:1. Cover centers the image, so half the overflow is cropped.
  let rx = 1
  let ry = 1
  if (imgAspect > frameAspect)
    rx = imgAspect / frameAspect // wider -> crop x
  else ry = frameAspect / imgAspect // taller -> crop y
  return { left: x * rx + (1 - rx) / 2, top: y * ry + (1 - ry) / 2 }
}
